import React from 'react';
import MainMenu from './MainMenu';
import StartScreen from './StartScreen';
import QuestionScreen from './QuestionScreen';
import FinishedScreen from './FinishedScreen';
import CreateLobby from './CreateLobby';
import JoinLobby from './JoinLobby';
import LobbyWaitingRoom from './LobbyWaitingRoom';
import MultiplayerQuestionScreen from './MultiplayerQuestionScreen';
import MultiplayerFinishedScreen from './MultiplayerFinishedScreen';

const GameRenderer = ({
  gameMode,
  gameLogic,
  multiplayerState,
  gameSettings,
  handlers
}) => {
  const {
    lobbyCode,
    players,
    playerName,
    isHost
  } = multiplayerState;

  const {
    selectedCategory,
    setSelectedCategory,
    selectedDifficulty,
    setSelectedDifficulty,
    questionCount,
    setQuestionCount,
    muted,
    setMuted
  } = gameSettings;

  const {
    handleQuickPlay,
    handleCreateLobby,
    handleJoinLobby,
    handleBackToMenu,
    handleCreateLobbySubmit,
    handleJoinLobbySubmit,
    handleStartLobbyGame,
    handleRefreshLobby
  } = handlers;

  // Render functions
  const renderMainMenu = () => (
    <MainMenu
      onQuickPlay={handleQuickPlay}
      onCreateLobby={handleCreateLobby}
      onJoinLobby={handleJoinLobby}
    />
  );

  const renderStartScreen = () => (
    <StartScreen
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
      selectedDifficulty={selectedDifficulty}
      setSelectedDifficulty={setSelectedDifficulty}
      questionCount={questionCount}
      setQuestionCount={setQuestionCount}
      muted={muted}
      setMuted={setMuted}
      startGame={gameLogic.startGame}
      loading={gameLogic.loading}
      error={gameLogic.error}
      onBack={handleBackToMenu}
    />
  );

  const renderQuestionScreen = () => (
    <QuestionScreen
      currentQuestion={gameLogic.currentQuestion}
      currentQuestionIndex={gameLogic.currentQuestionIndex}
      gameData={gameLogic.gameData}
      score={gameLogic.score}
      selectedAnswer={gameLogic.selectedAnswer}
      showFeedback={gameLogic.showFeedback}
      feedback={gameLogic.feedback}
      loading={gameLogic.loading}
      error={gameLogic.error}
      submitAnswer={gameLogic.submitAnswer}
      getAnswerButtonClass={gameLogic.getAnswerButtonClass}
    />
  );

  const renderFinishedScreen = () => (
    <FinishedScreen
      score={gameLogic.score}
      gameData={gameLogic.gameData}
      resetGame={handleBackToMenu}
    />
  );

  const renderCreateLobby = () => (
    <CreateLobby
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
      selectedDifficulty={selectedDifficulty}
      setSelectedDifficulty={setSelectedDifficulty}
      questionCount={questionCount}
      setQuestionCount={setQuestionCount}
      muted={muted}
      setMuted={setMuted}
      onCreateLobby={handleCreateLobbySubmit}
      loading={gameLogic.loading}
      error={gameLogic.error}
      onBack={handleBackToMenu}
    />
  );

  const renderJoinLobby = () => (
    <JoinLobby
      onJoinLobby={handleJoinLobbySubmit}
      loading={gameLogic.loading}
      error={gameLogic.error}
      onBack={handleBackToMenu}
    />
  );

  const renderLobbyWaitingRoom = () => (
    <LobbyWaitingRoom
      lobbyCode={lobbyCode}
      players={players}
      questionCount={questionCount}
      category={selectedCategory}
      difficulty={selectedDifficulty}
      isHost={isHost}
      onStartGame={handleStartLobbyGame}
      onLeaveLobby={handleBackToMenu}
      onRefreshLobby={handleRefreshLobby}
      loading={gameLogic.loading}
      error={gameLogic.error}
    />
  );

  const renderMultiplayerQuestionScreen = () => (
    <MultiplayerQuestionScreen
      currentQuestion={gameLogic.currentQuestion}
      currentQuestionIndex={gameLogic.currentQuestionIndex}
      gameData={gameLogic.gameData}
      score={gameLogic.score}
      selectedAnswer={gameLogic.selectedAnswer}
      showFeedback={gameLogic.showFeedback}
      feedback={gameLogic.feedback}
      loading={gameLogic.loading}
      error={gameLogic.error}
      submitAnswer={gameLogic.submitAnswer}
      getAnswerButtonClass={gameLogic.getAnswerButtonClass}
      players={players}
      playerName={playerName}
      onRefreshLobby={handleRefreshLobby}
    />
  );

  const renderMultiplayerFinishedScreen = () => {
    // Get the current player's score from the players array
    const currentPlayer = players.find(p => p.playerName === playerName);
    const playerScore = currentPlayer ? currentPlayer.score : gameLogic.score;
    
    return (
      <MultiplayerFinishedScreen
        score={playerScore}
        gameData={gameLogic.gameData}
        players={players}
        playerName={playerName}
        resetGame={handleBackToMenu}
        onRefreshLobby={handleRefreshLobby}
      />
    );
  };

  // Main render logic
  if (gameMode === 'menu') return renderMainMenu();
  if (gameMode === 'quickplay' && gameLogic.gameState === 'start') return renderStartScreen();
  if (gameMode === 'quickplay' && gameLogic.gameState === 'playing') return renderQuestionScreen();
  if (gameMode === 'quickplay' && gameLogic.gameState === 'finished') return renderFinishedScreen();
  if (gameMode === 'create-lobby') return renderCreateLobby();
  if (gameMode === 'join-lobby') return renderJoinLobby();
  if (gameMode === 'lobby-waiting') return renderLobbyWaitingRoom();
  if (gameMode === 'lobby-playing' && gameLogic.gameState === 'playing') return renderMultiplayerQuestionScreen();
  if (gameMode === 'lobby-playing' && gameLogic.gameState === 'finished') return renderMultiplayerFinishedScreen();

  // Fallback
  return renderMainMenu();
};

export default GameRenderer;
